import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useMotionPreferences } from "../hooks/use-motion-preferences";
import "./HeroE.css";

/* ────────────────────────────────────────────────────────────────
   Hero E — Editorial Corporate, photo-left / text-right (mirrored).
   The "magazine cover" pattern: image on the left, dignified type
   on the right. Same Editorial Corporate language as Hero D.
   ──────────────────────────────────────────────────────────────── */

function WordReveal({ children, delay = 0 }) {
  const words = String(children).split(" ");
  return (
    <span style={{ display: "inline" }}>
      {words.map((w, i) => (
        <span key={i} className="hero-e__word-clip">
          <motion.span
            className="hero-e__word"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function HeroE() {
  const { prefersReducedMotion } = useMotionPreferences();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section className="hero-e">
      <div className="hero-e__container">
        <motion.figure
          className="hero-e__figure"
          initial={prefersReducedMotion ? false : { opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease }}
        >
          <img
            src="/industries/hospitality-catering.jpg"
            alt="Adel Group placed Kenyan hospitality team at a Gulf hotel"
            className="hero-e__image"
            loading="eager"
            decoding="async"
          />
          <div className="hero-e__overlay-stat">
            <div className="hero-e__overlay-figure">5,000+</div>
            <div className="hero-e__overlay-label">
              Careers launched across the Gulf
            </div>
          </div>
        </motion.figure>

        <div className="hero-e__content">
          <motion.span
            className="hero-e__eyebrow"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease }}
          >
            Kenya &rarr; the Gulf &middot; Since 2015
          </motion.span>

          <h1 className="hero-e__title">
            <span className="hero-e__title-line">
              <WordReveal delay={0.18}>Connecting Talents.</WordReveal>
            </span>
            <span className="hero-e__title-line">
              <WordReveal delay={0.40}>Creating</WordReveal>{" "}
              <span className="hero-e__word-clip">
                <motion.span
                  className="hero-e__word hero-e__word--accent"
                  initial={prefersReducedMotion ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.55, ease }}
                >
                  Opportunities.
                </motion.span>
              </span>
            </span>
          </h1>

          <motion.p
            className="hero-e__sub"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease }}
          >
            Helping employers find the right talent and candidates the right
            job&mdash;every step of recruitment.
          </motion.p>

          <motion.div
            className="hero-e__ctas"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease }}
          >
            <Link to="/contact" className="hero-e__cta hero-e__cta--primary">
              Hire talent <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="/job-seekers" className="hero-e__cta hero-e__cta--ghost">
              Find work <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          </motion.div>

          <motion.div
            className="hero-e__trust"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.45, ease }}
          >
            <span>NEA licensed recruitment partner</span>
            <span className="hero-e__trust-dot" />
            <span>17 industries served</span>
            <span className="hero-e__trust-dot" />
            <span>Nairobi &middot; Riyadh &middot; Dubai</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
